import { useState } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@f2e/ui/components/base"

import { useToast } from "@/hooks"
import { useSignupApi } from "@/hooks/api/account"
import { getAuthPasswordErrorMessage } from "@/lib/authErrors"
import { getNewPasswordFormError } from "@/lib/passwordValidation"
import {
  PasswordBasicErrorMessage,
  PasswordRequirements,
} from "@/components/Auth"

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const Signup = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const { fetchFn: signup } = useSignupApi()
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    const trimmedEmail = email.trim()

    if (!trimmedName) {
      alertToast({
        title: t("common.auth.signup.errors.title"),
        description: t("common.auth.signup.errors.nameRequired"),
      })
      return
    }

    if (!trimmedEmail) {
      alertToast({
        title: t("common.auth.signup.errors.title"),
        description: t("common.auth.signup.errors.emailRequired"),
      })
      return
    }

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      alertToast({
        title: t("common.auth.signup.errors.title"),
        description: t("common.auth.signup.errors.invalidEmail"),
      })
      return
    }

    const passwordError = getNewPasswordFormError({
      password,
      confirmPassword,
    })
    if (passwordError) {
      const errorMessages = {
        passwordRequired: t("common.auth.signup.errors.passwordRequired"),
        confirmPasswordRequired: t(
          "common.auth.signup.errors.confirmPasswordRequired"
        ),
        passwordsDoNotMatch: t("common.auth.errors.passwordsDoNotMatch"),
        passwordPolicy: t("common.auth.errors.passwordPolicy"),
      }
      alertToast({
        title: t("common.auth.signup.errors.title"),
        description: errorMessages[passwordError],
      })
      return
    }

    try {
      await signup({
        jsonBody: {
          email: trimmedEmail,
          password,
          name: trimmedName,
        },
      })
      setSubmitted(true)
    } catch (error) {
      const message = getAuthPasswordErrorMessage(
        error,
        t,
        "common.auth.signup.errors.createFailed"
      )
      alertToast({
        title: t("common.auth.signup.errors.title"),
        description: message,
      })
    }
  }

  return (
    <div className="container flex min-h-[600px] items-center justify-center">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {t("common.auth.signup.title")}
          </CardTitle>
          <CardDescription>
            {t("common.auth.signup.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4 text-sm">
              <p>{t("common.auth.signup.sent")}</p>
              <Button className="w-full" onClick={() => navigate("/login")}>
                {t("common.auth.actions.backToLogin")}
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">{t("common.auth.fields.name")}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">{t("common.auth.fields.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="password">
                    {t("common.auth.fields.password")}
                  </Label>
                  <PasswordRequirements />
                </div>
                <Input
                  id="password"
                  type="password"
                  minLength={8}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <PasswordBasicErrorMessage
                  password={password}
                  show={password.length > 0}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="confirm-password">
                  {t("common.auth.fields.confirmPassword")}
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </div>
              <Button className="w-full" onClick={handleSubmit}>
                {t("common.auth.signup.submit")}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate("/login")}
              >
                {t("common.auth.actions.backToLogin")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default Signup
